import { motion } from "framer-motion";
import {
  UserPlus,
  Users,
  PartyPopper,
  Trophy,
  Heart,
  ThumbsUp,
  Flame,
  Crown,
  Smile,
  type LucideIcon,
} from "lucide-react";
import { Reveal } from "../ui/Reveal";
import { SectionHeading } from "../ui/SectionHeading";
import { Wave, Spark } from "../ui/decor";
import { COMMUNITY } from "../../content/site";

const ICONS: Record<string, LucideIcon> = {
  userPlus: UserPlus,
  users: Users,
  party: PartyPopper,
  trophy: Trophy,
};

// One flat pastel per social pillar so the stack reads like a hand of swatches.
const PASTELS = ["bg-teal-mist", "bg-sun-pale", "bg-coral-pale", "bg-violet-mist"];

/** A single milestone post in the group-feed mock. */
function FeedPost({
  initials,
  ring,
  name,
  event,
  hearts,
  thumbs,
}: {
  initials: string;
  ring: string;
  name: string;
  event: string;
  hearts: number;
  thumbs: number;
}) {
  return (
    <div className="rounded-2xl border-2 border-navy bg-white p-3.5">
      <div className="flex items-start gap-3">
        <span
          className={`grid h-9 w-9 shrink-0 place-items-center rounded-full border-2 border-navy font-display text-xs font-extrabold text-navy ${ring}`}
        >
          {initials}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-bold leading-tight text-navy">{name}</p>
          <p className="text-xs leading-snug text-charcoal/70">{event}</p>
        </div>
      </div>
      <div className="mt-2.5 flex items-center gap-2 pl-12">
        <span className="inline-flex items-center gap-1 rounded-full border-2 border-navy bg-coral-pale px-2 py-0.5 text-[11px] font-bold text-navy">
          <Heart className="h-3 w-3 fill-coral text-coral" /> {hearts}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full border-2 border-navy bg-teal-mist px-2 py-0.5 text-[11px] font-bold text-navy">
          <ThumbsUp className="h-3 w-3 text-teal" /> {thumbs}
        </span>
      </div>
    </div>
  );
}

export function Community() {
  return (
    <section id="community" className="relative overflow-hidden bg-violet-deep">
      {/* Melt into the cream page above and below. */}
      <Wave fill="#FFF6EC" flip />

      {/* Playful floating accents on the flat violet block. */}
      <Spark
        fill="#FFC93C"
        className="absolute left-[6%] top-28 hidden h-10 w-10 animate-float sm:block"
      />
      <Spark
        fill="#FFF6EC"
        className="absolute bottom-32 right-[7%] hidden h-7 w-7 animate-wiggle lg:block"
      />

      <div className="container-x relative py-16 sm:py-24">
        <SectionHeading
          light
          eyebrow="You're not doing this alone"
          title={
            <>
              Progress is better with a{" "}
              <span className="marker text-navy">crowd</span> behind you
            </>
          }
          sub="Invite a friend, find your people, and let every win get the cheer it deserves. It's the part that keeps you showing up long after motivation fades — and it's free from day one."
        />

        <div className="mt-14 grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* The four social pillars, as pop cards */}
          <Reveal>
            <ul className="space-y-4">
              {COMMUNITY.map((item, i) => {
                const Icon = ICONS[item.icon] ?? Users;
                return (
                  <li
                    key={item.title}
                    className={`flex items-start gap-4 rounded-[1.5rem] border-[3px] border-navy p-5 shadow-pop-sm ${PASTELS[i % PASTELS.length]} ${
                      i % 2 === 0 ? "rotate-[0.5deg]" : "-rotate-[0.5deg]"
                    }`}
                  >
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full border-[3px] border-navy bg-white">
                      <Icon className="h-6 w-6 text-navy" strokeWidth={2.25} />
                    </span>
                    <div>
                      <h3 className="font-display text-lg font-extrabold text-navy">
                        {item.title}
                      </h3>
                      <p className="mt-1 text-sm leading-relaxed text-navy/75">
                        {item.body}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </Reveal>

          {/* Group-feed + leaderboard mock */}
          <Reveal delay={1}>
            <div className="relative mx-auto w-full max-w-sm">
              {/* Group feed card */}
              <div className="rotate-1 rounded-[1.75rem] border-[3px] border-navy bg-cream p-4 shadow-pop">
                <div className="mb-3 flex items-center justify-between">
                  <span className="font-display text-sm font-extrabold text-navy">
                    🍕 Pizza Lovers · Plymouth
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border-2 border-navy bg-white px-2 py-0.5 text-[11px] font-bold text-navy">
                    <Users className="h-3 w-3" /> 248
                  </span>
                </div>

                <div className="space-y-2.5">
                  <FeedPost
                    initials="SM"
                    ring="bg-teal-mist"
                    name="Sarah M."
                    event="Hit a new all-time low — down 6.2 kg! 🎉"
                    hearts={24}
                    thumbs={11}
                  />
                  <FeedPost
                    initials="JT"
                    ring="bg-sun"
                    name="James T."
                    event="30-day streak. Not missed a morning."
                    hearts={18}
                    thumbs={9}
                  />
                </div>

                {/* Group composer — a hint of the moderated group chat. */}
                <div className="mt-3 flex items-center gap-2 rounded-full border-2 border-navy bg-white px-3 py-2">
                  <Smile className="h-4 w-4 shrink-0 text-violet" />
                  <span className="text-xs text-charcoal/50">Cheer them on…</span>
                  <span className="ml-auto grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 border-navy bg-teal text-white">
                    <Heart className="h-3 w-3 fill-current" />
                  </span>
                </div>
              </div>

              {/* Mini leaderboard card, overlapping the corner */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 5, repeat: Infinity }}
                className="absolute -bottom-10 -left-4 z-20 w-48 -rotate-2 rounded-2xl border-[3px] border-navy bg-white p-3 shadow-pop-sm sm:-left-8"
              >
                <div className="mb-2 flex items-center gap-1.5">
                  <Trophy className="h-4 w-4 text-violet" />
                  <span className="font-display text-xs font-extrabold uppercase tracking-wide text-navy">
                    This week
                  </span>
                </div>
                {[
                  ["1", "You", "9,400"],
                  ["2", "Priya", "8,100"],
                  ["3", "Marc", "7,650"],
                ].map(([rank, who, cal], idx) => (
                  <div
                    key={who}
                    className={`flex items-center justify-between rounded-lg px-2 py-1 text-xs ${
                      idx === 0 ? "bg-sun-pale font-bold text-navy" : "text-charcoal/75"
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      {idx === 0 ? (
                        <Crown className="h-3.5 w-3.5 text-sun" />
                      ) : (
                        <span className="num w-3.5 text-center text-charcoal/50">
                          {rank}
                        </span>
                      )}
                      {who}
                    </span>
                    <span className="num font-bold text-teal">{cal}</span>
                  </div>
                ))}
              </motion.div>

              {/* Badge sticker, top corner */}
              <motion.span
                aria-hidden
                animate={{ y: [0, 7, 0] }}
                transition={{ duration: 6, repeat: Infinity, delay: 0.5 }}
                className="sticker absolute -right-3 -top-5 z-20 rotate-6 !bg-white"
              >
                <Flame className="h-4 w-4 text-coral" /> 100-day badge
              </motion.span>

              {/* Celebration sticker, bottom-right corner */}
              <span
                aria-hidden
                className="sticker absolute -bottom-6 -right-2 z-20 -rotate-3 sm:-right-5"
              >
                <PartyPopper className="h-4 w-4 text-violet" /> Cheers only
              </span>
            </div>
          </Reveal>
        </div>
      </div>

      <Wave fill="#FFF6EC" />
    </section>
  );
}
